import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { doc } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import LoadingSpinner from '../loading-spinner/loading-spinner';
import ErrorDisplay from '../error-display/error-display';
import useFirestoreQuery from '../../hooks/useFirestoreQuery';
import './blog-display.css';
import 'bootstrap-icons/font/bootstrap-icons.css';

const BlogDisplay = () => {
  const { blogId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Determine where the user came from (calendar or blog list)
  const fromCalendar = new URLSearchParams(location.search).get('from') === 'calendar';
  
  // Function to handle back button click
  const handleBack = () => {
    if (fromCalendar) {
      navigate('/calendar');
    } else {
      navigate('/blogs');
    }
  };

  // Use our custom hook to fetch the blog post
  const { data: blog, loading, error } = useFirestoreQuery({
    queryFn: () => doc(db, 'blogs', blogId),
    dependencies: [blogId],
    transformFn: (docSnap) => {
      if (!docSnap.exists()) return null;
      
      const blogData = docSnap.data();
      
      // Only show published blogs to the public
      if (!blogData.isPublished) {
        throw new Error('This blog post is not available.');
      }
      
      return {
        ...blogData,
        id: docSnap.id,
      };
    }
  });

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="container mt-4">
        <ErrorDisplay message={error} />
        <Link to="/blogs" className="btn btn-primary">
          Back to Blogs
        </Link>
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="container mt-4">
        <ErrorDisplay message="Blog post not found" type="warning" />
        <Link to="/blogs" className="btn btn-primary">
          Back to Blogs
        </Link>
      </div>
    );
  }

  return (
    <div className="container mt-4">
      {/* Back button for mobile/smaller screens */}
      <div className="d-block d-md-none mb-3">
        <button
          className="btn btn-outline-danger"
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Back to {fromCalendar ? 'Calendar' : 'Blogs'}
        </button>
      </div>
      
      <article>
        <header className="mb-4">
          <h1 className="fw-bolder mb-1">{blog.title}</h1>
          <div className="text-muted fst-italic mb-2">
            Posted on {formatDate(blog.createdAt, 'DATE_MED')}
          </div>
          
          {/* Display event details if available */}
          {blog.eventDate && (
            <div className="alert alert-info mb-3">
              <div className="d-flex align-items-center">
                <i className="bi bi-calendar-event me-2 fs-4"></i>
                <div>
                  <strong>Start:</strong> {formatDateTime(blog.eventDate)}
                  
                  {blog.eventEndDate && (
                    <div className="mt-1">
                      <strong>End:</strong> <i className="bi bi-clock me-1"></i> {formatDateTime(blog.eventEndDate)}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </header>
        
        {/* Display image if available */}
        {blog.imageUrl && (
          <div className="mb-4">
            <img
              src={blog.imageUrl}
              alt={blog.title}
              className="img-fluid rounded"
              style={{ maxHeight: '500px', width: 'auto', display: 'block', margin: '0 auto' }}
            />
          </div>
        )}
        
        <section className="mb-5">
          <div
            className="fs-5 mb-4 blog-content"
            dangerouslySetInnerHTML={{ __html: blog.content }}
          />
        </section>
      </article>
      
      <div className="mt-4">
        <button
          className="btn bg-red text-white"
          onClick={handleBack}
        >
          <i className="bi bi-arrow-left me-1"></i>
          Back to {fromCalendar ? 'Calendar' : 'Blogs'}
        </button>
      </div>
    </div>
  );
};

export default BlogDisplay;